import { useState } from "react";
import { Link, Redirect } from "wouter";
import { trpc } from "../../_core/trpc";
import { useAuth } from "../../_core/useAuth";
import HeartScene from "../../components/three/HeartScene";
function AdminLogin() {
  const { user, login, signup, logout } = useAuth();
  const status = trpc.auth.adminStatus.useQuery();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const claimed = status.data?.adminExists ?? true;
  const effectiveMode = claimed ? "login" : "claim";
  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (effectiveMode === "claim") {
        if (password.length < 8) throw new Error("Choose a password of at least 8 characters.");
        await signup(email, password, name || void 0);
      } else {
        await login(email, password);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn't work. Please try again.");
    } finally {
      setBusy(false);
    }
  }
  if (user && status.data?.isAdmin) return <Redirect to="/admin" />;
  return <main className="vignette relative grid min-h-[100svh] place-items-center overflow-hidden px-4 py-10">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <HeartScene intensity={1} mode="teaser" className="absolute inset-0" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-6 text-center">
          <Link to="/" className="eyebrow hover:text-[#e9a8f2]">
            ← Back to the site
          </Link>
        </div>

        <div className="glass gold-frame rounded-2xl p-7 sm:p-9">
          <h1 className="display-md gold-text">
            {effectiveMode === "claim" ? "Claim this site" : "Owner sign-in"}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-[color:var(--muted-foreground)]">
            {effectiveMode === "claim" ? "This site has no owner yet. Create the owner account now \u2014 it will be the only one with admin access. Choose an email and password you will remember." : "Sign in to edit the letter, photos, video, music and the midnight schedule."}
          </p>

          {
    /*
      The first question anyone asks here: "what admin email and password do
      I use?" Answer it on the screen instead of leaving it to be guessed at.
      Both branches stay honest: the owner account exists once claimed, and
      this app has no email, so it cannot and must not promise a reset link.
    */
  }
          <div className="mt-4 rounded-xl border border-[rgba(233,168,242,.26)] bg-[rgba(233,168,242,.07)] p-4 text-sm leading-relaxed">
            <p className="eyebrow">Which email and password?</p>
            <p className="mt-2 text-[color:var(--muted-foreground)]">
              {effectiveMode === "claim" ? <>
                  This site has no owner yet. Whatever you type into the two fields below{" "}
                  <em>becomes</em> the owner login — use an address you can actually open, and a
                  password of at least 8 characters.
                </> : <>
                  The owner account is already set up. Sign in with your owner email and password.
                  You can change the password at any time under{" "}
                  <strong className="text-[#e6cdf5]">Settings → Change password</strong>.
                </>}
            </p>
          </div>

          {user && status.data && !status.data.isAdmin && <div className="mt-5 rounded-xl border border-[rgba(244,166,198,.32)] bg-[rgba(91,33,182,.18)] p-4 text-sm">
              <p className="text-[#f3c2ce]">
                You are signed in as <strong>{user.email}</strong>, but this account is not an
                admin for this site.
              </p>
              <button
    type="button"
    onClick={() => void logout()}
    className="mt-3 text-xs uppercase tracking-[0.18em] text-[#e9a8f2] underline"
  >
                Sign out and use another account
              </button>
            </div>}

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-xs uppercase tracking-[0.16em] text-[color:var(--muted-foreground)]">
                Email
              </label>
              <input data-testid="email-input"
    id="email"
    type="email"
    autoComplete="username"
    required
    value={email}
    onChange={(e) => setEmail(e.target.value)}
    className="field-lux"
    placeholder="you@example.com"
  />
            </div>

            {effectiveMode === "claim" && <div>
                <label htmlFor="name" className="mb-1.5 block text-xs uppercase tracking-[0.16em] text-[color:var(--muted-foreground)]">
                  Your name <span className="opacity-60">(optional)</span>
                </label>
                <input data-testid="name-input"
    id="name"
    type="text"
    autoComplete="name"
    value={name}
    onChange={(e) => setName(e.target.value)}
    className="field-lux"
    placeholder="Your name"
  />
              </div>}

            <div>
              <label htmlFor="password" className="mb-1.5 block text-xs uppercase tracking-[0.16em] text-[color:var(--muted-foreground)]">
                Password
              </label>
              <input data-testid="password-input"
    id="password"
    type="password"
    autoComplete={effectiveMode === "claim" ? "new-password" : "current-password"}
    required
    minLength={effectiveMode === "claim" ? 8 : void 0}
    value={password}
    onChange={(e) => setPassword(e.target.value)}
    className="field-lux"
    placeholder={effectiveMode === "claim" ? "At least 8 characters" : "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
  />
            </div>

            {error && <p role="alert" className="rounded-lg border border-[rgba(244,63,94,.4)] bg-[rgba(244,63,94,.14)] px-3 py-2 text-sm text-[#ffc9cd]">
                {error}
              </p>}

            <button data-testid="login-submit-btn" type="submit" className="btn-gold w-full" disabled={busy}>
              {busy ? "Working\u2026" : effectiveMode === "claim" ? "Create owner account" : "Sign in"}
            </button>
          </form>

          {effectiveMode === "claim" && <p className="mt-4 text-[0.7rem] leading-relaxed text-[color:var(--muted-foreground)]">
              Once the owner account exists, nobody else can claim this site. You can change the
              password later under Settings → Change password.
            </p>}
        </div>

        {status.isLoading && <p className="mt-4 text-center text-xs text-[color:var(--muted-foreground)]">Checking…</p>}
      </div>
    </main>;
}
export {
  AdminLogin as default
};
